/**
 * Visibility-change handler — reconcile workspace state when the tab regains focus.
 *
 * On tab refocus (after >5s hidden), refetch bootstrap and compare:
 *   - If `workspaceRevision` differs → the bootstrap replaces store + IDB
 *   - If same → no-op
 *
 * Replaces the visibility handlers in useNamedLayouts and useLayoutTree.
 */

import type { WorkspaceStore } from './store'
import type { EnqueueBootstrapOpts } from './bootstrap'

export interface RevisionSyncOptions {
  scope: string
  store: WorkspaceStore
  /** Minimum hidden duration before refetch (ms). Defaults to 5000. */
  minHiddenMs?: number
  /**
   * Queued bootstrap runner. MUST be the shared `enqueueBootstrap` from
   * `makeBootstrapQueue`, not `runBootstrap`: the SerialQueue exists to stop
   * two bootstraps race-writing IDB, and a refocus is one of the three paths
   * that can collide (init, inbound broadcast, refocus).
   */
  enqueueBootstrap: (opts: EnqueueBootstrapOpts) => Promise<unknown>
  /**
   * Returns true while a local mutation is in flight or queued.
   * Visibility-change refetches are skipped to avoid clobbering optimistic
   * state.
   */
  isBusy?: () => boolean
}

export interface RevisionSyncBinding {
  close: () => void
}

export function attachRevisionSync(opts: RevisionSyncOptions): RevisionSyncBinding {
  if (typeof document === 'undefined') {
    return { close: () => {} }
  }

  // 5s, matching this file's header, the JSDoc above, the README, and the
  // content store's own hardcoded threshold. It read 15_000, so the two halves
  // of a single tab woke on different schedules.
  const minHiddenMs = opts.minHiddenMs ?? 5_000
  let hiddenAt = 0

  const handler = () => {
    if (document.hidden) {
      hiddenAt = Date.now()
      return
    }
    if (Date.now() - hiddenAt < minHiddenMs) return
    if (opts.store.getState().phase.kind === 'Stale') {
      // Stale controller — skip refetch. Reconciling against a server
      // bootstrap on top of divergent local state would produce a hybrid
      // that exists in no tab. User must reload.
      return
    }
    if (opts.isBusy?.()) {
      // A local mutation is in flight or queued — let it land first.
      return
    }

    void opts
      .enqueueBootstrap({
        scope: opts.scope,
        store: opts.store,
        preserveActiveLayout: true,
      })
      .catch(() => {
        /* swallow — bootstrap error already updated connectionStatus */
      })
  }

  document.addEventListener('visibilitychange', handler)

  return {
    close: () => document.removeEventListener('visibilitychange', handler),
  }
}
